import gsap from 'gsap';
import Order from './order';
import Utils from '../../assets/scripts/utils/utils';
import { emailRules, getTelRules, nameRules } from '../../assets/scripts/utils/validationRules';

class OrderRecipient {
	constructor() {
		this.init();
	}

	init() {
		this.container = document.querySelector('[data-order-recipient]');

		if (!this.container) return;

		this.card = this.container.querySelector('.order-section__card');
		this.cardContentWrapper = this.card?.querySelector('.checkout-card__content-wrapper');
		this.cardMainContent = this.card?.querySelector('[data-card-content="recipient"]');
		this.cardOtherContent = this.card?.querySelector('[data-card-content="other"]');

		this.userSection = this.container.querySelector('.order-recipient__user');
		this.otherRecipientSection = this.container.querySelector('.order-recipient__other');
		this.otherRecipientCheckbox = this.container.querySelector('.checkbox._other-recipient input');
		this.bonusCheckbox = this.container.querySelector('.checkbox._bonus input');

		// Skip auto-section validation when simply toggling the "other recipient" option
		this.otherRecipientCheckbox?.setAttribute('data-ignore', 'true');

		this.otherRecipientSection.classList.toggle('_hidden', !this.otherRecipientCheckbox.checked);
		gsap.set(this.otherRecipientSection, { height: 0, overflow: 'hidden' });

		const otherRecipientTelInput = this.container.querySelector('#other-recipient-tel');

		otherRecipientTelInput?.addEventListener('countrychange', () => {
			setTimeout(() => {
				Order._validateSection();
			}, 50);
		});
		otherRecipientTelInput?.addEventListener('blur', () => {
			setTimeout(() => {
				Order._validateSection();
			}, 0);
		});

		setTimeout(() => {
			this._initInputs();
		}, 1000);

		this._initValidation();
	}

	_initInputs() {
		const userSectionInputs = {
			name: this.userSection.querySelector('#recipient-name'),
			surname: this.userSection.querySelector('#recipient-surname'),
			email: this.userSection.querySelector('#recipient-email'),
			tel: this.userSection.querySelector('#recipient-tel'),
		};
		const otherRecipientSectionInputs = {
			name: this.otherRecipientSection.querySelector('#other-recipient-name'),
			surname: this.otherRecipientSection.querySelector('#other-recipient-surname'),
			tel: this.otherRecipientSection.querySelector('#other-recipient-tel'),
		};
		const getUserTel = (input) =>
			input ? `${input.getAttribute('data-country-code') || ''} ${input.mask?.value || input.value || ''}`.trim() : '';

		this.person = {
			name: userSectionInputs.name.value,
			surname: userSectionInputs.surname.value,
			email: userSectionInputs.email.value,
			tel: getUserTel(userSectionInputs.tel),
		};

		this.otherPerson = {
			name: otherRecipientSectionInputs.name.value,
			surname: otherRecipientSectionInputs.surname.value,
			email: '',
			tel: getUserTel(otherRecipientSectionInputs.tel),
		};

		if (this.userSection.querySelector('#recipient-tel').value) {
			const timer = setInterval(() => {
				this.person = {
					name: userSectionInputs.name.value,
					surname: userSectionInputs.surname.value,
					email: userSectionInputs.email.value,
					tel: getUserTel(userSectionInputs.tel),
				};

				this.otherPerson = {
					name: otherRecipientSectionInputs.name.value,
					surname: otherRecipientSectionInputs.surname.value,
					email: '',
					tel: getUserTel(otherRecipientSectionInputs.tel),
				};

				this.updateCard(this.person, false);
				this.updateCard(this.otherPerson, true);

				if (userSectionInputs.tel.getAttribute('data-country-code') && userSectionInputs.tel.mask?.value) {
					clearInterval(timer);
				}
			}, 1000);
		}

		userSectionInputs.name.addEventListener('change', () => {
			this.updateCard(
				{
					...this.person,
					name: userSectionInputs.name.value,
					surname: userSectionInputs.surname.value,
				},
				false,
			);
		});
		userSectionInputs.surname.addEventListener('change', () => {
			this.updateCard(
				{
					...this.person,
					name: userSectionInputs.name.value,
					surname: userSectionInputs.surname.value,
				},
				false,
			);
		});
		userSectionInputs.email.addEventListener('change', () => {
			this.updateCard(
				{
					...this.person,
					email: userSectionInputs.email.value,
				},
				false,
			);
		});
		userSectionInputs.tel.addEventListener('change', (evt) => {
			this.updateCard(
				{
					...this.person,
					tel: getUserTel(evt.target),
				},
				false,
			);
		});
		userSectionInputs.tel.addEventListener('input', (evt) => {
			this.updateCard(
				{
					...this.person,
					tel: getUserTel(evt.target),
				},
				false,
			);
		});
		userSectionInputs.tel.addEventListener('countrychange', (evt) => {
			this.updateCard(
				{
					...this.person,
					tel: getUserTel(evt.target),
				},
				false,
			);
		});

		otherRecipientSectionInputs.name.addEventListener('change', () => {
			this.updateCard(
				{
					...this.otherPerson,
					name: otherRecipientSectionInputs.name.value,
					surname: otherRecipientSectionInputs.surname.value,
				},
				true,
			);
		});
		otherRecipientSectionInputs.surname.addEventListener('change', () => {
			this.updateCard(
				{
					...this.otherPerson,
					name: otherRecipientSectionInputs.name.value,
					surname: otherRecipientSectionInputs.surname.value,
				},
				true,
			);
		});
		otherRecipientSectionInputs.tel.addEventListener('change', (evt) => {
			this.updateCard(
				{
					...this.otherPerson,
					tel: getUserTel(evt.target),
				},
				true,
			);
		});
		otherRecipientSectionInputs.tel.addEventListener('blur', (evt) => {
			setTimeout(() => {
				this.updateCard(
					{
						...this.otherPerson,
						tel: getUserTel(evt.target),
					},
					true,
				);
			}, 500);
		});
		otherRecipientSectionInputs.tel.addEventListener('countrychange', (evt) => {
			setTimeout(() => {
				this.updateCard(
					{
						...this.otherPerson,
						tel: getUserTel(evt.target),
					},
					true,
				);
			}, 500);
		});

		this.updateCard(this.person, false);
		this.updateCard(this.otherPerson, true);
	}

	_initValidation() {
		Order.validator
			.addField('#recipient-name', nameRules, {
				errorsContainer: Utils.getErrorContainer('#recipient-name'),
			})
			.addField('#recipient-surname', nameRules, {
				errorsContainer: Utils.getErrorContainer('#recipient-surname'),
			})
			.addField('#recipient-email', emailRules, {
				errorsContainer: Utils.getErrorContainer('#recipient-email'),
			})
			.addField('#recipient-tel', [{ rule: 'required' }], {
				errorsContainer: Utils.getErrorContainer('#recipient-tel'),
			});

		const birthdateInput = this.container.querySelector('#recipient-birthdate');

		if (this.bonusCheckbox && this.bonusCheckbox.checked && birthdateInput) {
			Order.validator.addField(
				'#recipient-birthdate',
				[
					{ rule: 'required' },
					{
						rule: 'minLength',
						value: 10,
						errorMessage: 'Неверный формат',
					},
				],
				{
					errorsContainer: Utils.getErrorContainer('#recipient-birthdate'),
				},
			);
		} else {
			if (birthdateInput) {
				Order.validator.addField(
					'#recipient-birthdate',
					[
						{
							rule: 'minLength',
							value: 10,
							errorMessage: 'Неверный формат',
						},
					],
					{
						errorsContainer: Utils.getErrorContainer('#recipient-birthdate'),
					},
				);
			}
		}

		this.bonusCheckbox?.addEventListener('change', async () => {
			const isChecked = this.bonusCheckbox.checked;

			if (isChecked) {
				Order.validator.addField(
					'#recipient-birthdate',
					[
						{ rule: 'required' },
						{
							rule: 'minLength',
							value: 10,
							errorMessage: 'Неверный формат',
						},
					],
					{
						errorsContainer: Utils.getErrorContainer('#recipient-birthdate'),
					},
				);
				const isValid = await Order.validator.revalidateField('#recipient-birthdate');
				if (!isValid) {
					birthdateInput.focus();
				}
			} else {
				Order.validator.addField(
					'#recipient-birthdate',
					[
						{
							rule: 'minLength',
							value: 10,
							errorMessage: 'Неверный формат',
						},
					],
					{
						errorsContainer: Utils.getErrorContainer('#recipient-birthdate'),
					},
				);
			}
		});

		this.otherRecipientCheckbox.addEventListener('change', () => {
			const isOtherRecipient = this.otherRecipientCheckbox.checked;

			gsap.to(this.otherRecipientSection, {
				autoAlpha: isOtherRecipient ? 1 : 0,
				height: isOtherRecipient ? 'auto' : 0,
				duration: 0.3,
				onStart: () => {
					gsap.set(this.otherRecipientSection, {
						overflow: 'hidden',
					});
				},
				onComplete: () => {
					gsap.set(this.otherRecipientSection, {
						overflow: isOtherRecipient ? null : 'hidden',
					});
				},
			});

			this.updateCard(this.person, false);
			this.updateCard(this.otherPerson, true);

			const clearOtherRecipientErrors = () => {
				const otherNameErrors = Utils.getErrorContainer('#other-recipient-name');
				const otherSurnameErrors = Utils.getErrorContainer('#other-recipient-surname');
				const otherTelErrors = Utils.getErrorContainer('#other-recipient-tel');

				if (otherNameErrors) otherNameErrors.textContent = '';
				if (otherSurnameErrors) otherSurnameErrors.textContent = '';
				if (otherTelErrors) otherTelErrors.textContent = '';
			};

			if (isOtherRecipient) {
				Order.validator
					.addField('#other-recipient-name', nameRules, {
						errorsContainer: Utils.getErrorContainer('#other-recipient-name'),
					})
					.addField('#other-recipient-surname', nameRules, {
						errorsContainer: Utils.getErrorContainer('#other-recipient-surname'),
					})
					.addField('#other-recipient-tel', getTelRules('#other-recipient-tel'), {
						errorsContainer: Utils.getErrorContainer('#other-recipient-tel'),
					});
			} else {
				if (Order.validator.fieldIds.has('#other-recipient-name')) {
					Order.validator.removeField('#other-recipient-name');
					Order.validator.fieldIds.delete('#other-recipient-name');
					Order.validator.removeField('#other-recipient-surname');
					Order.validator.fieldIds.delete('#other-recipient-surname');
					Order.validator.removeField('#other-recipient-tel');
					Order.validator.fieldIds.delete('#other-recipient-tel');
				}
				clearOtherRecipientErrors();
			}

			// Re-run validation to reflect the new set of required fields
			setTimeout(() => Order._validateSection(), 0);
		});
	}

	updateCard(person, isOtherRecipient = false) {
		if (!this.card) return;

		const targetContent = isOtherRecipient ? this.cardOtherContent : this.cardMainContent;
		const cardTitle = targetContent?.querySelector(`[data-card-title="${isOtherRecipient ? 'other' : 'recipient'}"]`);
		const cardTel = targetContent?.querySelector(`[data-card-tel="${isOtherRecipient ? 'other' : 'recipient'}"]`);
		const cardEmail = targetContent?.querySelector(`[data-card-email="${isOtherRecipient ? 'other' : 'recipient'}"]`);
		const fullName = [person.name, person.surname].filter(Boolean).join(' ');

		if (cardTitle) {
			cardTitle.textContent = fullName;
		}

		if (cardTel) {
			cardTel.textContent = person.tel || '';
		}

		if (cardEmail && !isOtherRecipient) {
			cardEmail.textContent = person.email || '';
		}

		if (isOtherRecipient) {
			this.otherPerson = person;
		} else {
			this.person = person;
		}

		this._toggleOtherCard();
	}

	_toggleOtherCard() {
		const isOtherRecipient = this.otherRecipientCheckbox?.checked;

		if (!this.cardOtherContent) return;

		this.cardOtherContent.classList.toggle('_hidden', !isOtherRecipient);
		this.cardContentWrapper?.classList.toggle('_has-other', isOtherRecipient);
	}
}

export default new OrderRecipient();
